import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAssigneeRanking, sortMembersByAssignmentFrequency, type RankingContext } from '../../hooks/support/useAssigneeRanking';
import { getInitialsFirstLast } from './initials';

export interface AssignmentMember {
  userId: string;
  displayName: string;
  email?: string;
  avatarUrl?: string;
}

export interface AssignmentSelectorProps {
  /** Currently assigned user ID */
  currentAssigneeId?: string | null;
  /** Currently assigned user name (for display) */
  currentAssigneeName?: string;
  /** List of team members to choose from */
  members: AssignmentMember[];
  /** Called when assignment changes */
  onAssign: (member: AssignmentMember | null) => Promise<void> | void;
  /** Whether mutation is in progress */
  isLoading?: boolean;
  /** Disable the selector */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Label to show when unassigned */
  unassignedLabel?: string;
  /** Compact trigger style for inline/list usage */
  compact?: boolean;
  /** Context for ranking assignees — determines which collection counts to use */
  rankingContext?: RankingContext;
}

/**
 * Unified assignment selector dropdown component.
 *
 * Used across call logs, cases, and work queue items for consistent
 * assignee selection. Features search, initials avatars, and checkmark
 * on the current selection.
 */
export function AssignmentSelector({
  currentAssigneeId,
  currentAssigneeName,
  members,
  onAssign,
  isLoading = false,
  disabled = false,
  className = '',
  unassignedLabel = 'Unassigned',
  compact = false,
  rankingContext = 'support',
}: AssignmentSelectorProps) {
  const { user } = useAuth();
  const currentUserId = user?.userId ?? '';
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus();
  }, [isOpen]);

  const handleSelect = async (member: AssignmentMember | null) => {
    if (member?.userId === currentAssigneeId || (!member && !currentAssigneeId)) {
      setIsOpen(false);
      setSearch('');
      return;
    }

    setIsSaving(true);
    try {
      await onAssign(member);
      setIsOpen(false);
      setSearch('');
    } catch (error) {
      console.error('Failed to assign:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Sort members by assignment frequency, excluding current user (shown as "Me")
  const { data: ranking } = useAssigneeRanking(rankingContext);
  const currentUserMember = useMemo(
    () => members.find(m => m.userId === currentUserId),
    [members, currentUserId],
  );
  const rankedMembers = useMemo(
    () => sortMembersByAssignmentFrequency(
      members.filter(m => m.userId !== currentUserId),
      ranking,
    ),
    [members, ranking, currentUserId]
  );

  const filtered = search
    ? rankedMembers.filter((m) => {
        const term = search.toLowerCase();
        return (
          m.displayName.toLowerCase().includes(term) ||
          m.email?.toLowerCase().includes(term)
        );
      })
    : rankedMembers;

  const displayName = currentAssigneeName || unassignedLabel;
  const isPending = isLoading || isSaving;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger button */}
      {compact ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) setIsOpen(!isOpen);
          }}
          disabled={disabled || isPending}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors min-h-[36px] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentAssigneeId && currentAssigneeName ? (
            <>
              <div className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-[10px] font-medium">
                {getInitialsFirstLast(currentAssigneeName)}
              </div>
              <span className="hidden sm:inline">{currentAssigneeName}</span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Assign</span>
            </>
          )}
          <ChevronDown className="w-3 h-3" />
        </button>
      ) : (
        <button
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || isPending}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-300 shadow-sm hover:border-slate-300 bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              currentAssigneeId
                ? 'bg-sky-100 text-sky-700'
                : 'bg-slate-200 text-slate-600'
            }`}
          >
            {currentAssigneeName ? getInitialsFirstLast(currentAssigneeName) : '?'}
          </div>
          <span className="font-medium text-slate-900">{displayName}</span>
          <ChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-slate-200 z-50 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full text-sm text-slate-700 outline-none placeholder:text-slate-400"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsOpen(false);
                  setSearch('');
                }
              }}
            />
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {/* Unassigned option */}
            {!search && (
              <button
                onClick={(e) => { e.stopPropagation(); handleSelect(null); }}
                disabled={isPending}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors disabled:opacity-50 ${
                  !currentAssigneeId ? 'bg-sky-50' : ''
                }`}
              >
                <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-400">
                  ?
                </div>
                <span className="text-slate-700">{unassignedLabel}</span>
                {!currentAssigneeId && <Check className="w-4 h-4 text-sky-600 ml-auto" />}
              </button>
            )}

            {/* Me option */}
            {!search && currentUserMember && (
              <button
                onClick={(e) => { e.stopPropagation(); handleSelect(currentUserMember); }}
                disabled={isPending}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors disabled:opacity-50 ${
                  currentAssigneeId === currentUserId ? 'bg-sky-50' : ''
                }`}
              >
                <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center text-xs font-medium text-sky-700">
                  {getInitialsFirstLast(currentUserMember.displayName)}
                </div>
                <span className="text-slate-700 font-medium">Me</span>
                <span className="text-xs text-slate-400">({currentUserMember.displayName})</span>
                {currentAssigneeId === currentUserId && <Check className="w-4 h-4 text-sky-600 ml-auto" />}
              </button>
            )}

            {/* Divider */}
            {!search && filtered.length > 0 && <div className="border-t border-slate-100 my-1" />}

            {/* Team members */}
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500 text-center">
                {search ? 'No matching users' : 'No team members found'}
              </div>
            ) : (
              filtered.map((member) => (
                <button
                  key={member.userId}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleSelect(member); }}
                  disabled={isPending}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors disabled:opacity-50 ${
                    currentAssigneeId === member.userId ? 'bg-sky-50' : ''
                  }`}
                >
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl}
                      alt={member.displayName}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center text-xs font-medium text-sky-700">
                      {getInitialsFirstLast(member.displayName)}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {member.displayName}
                    </div>
                    {member.email && member.email.includes('@') && (
                      <div className="text-xs text-slate-500 truncate">{member.email}</div>
                    )}
                  </div>

                  {currentAssigneeId === member.userId && (
                    <Check className="w-4 h-4 text-sky-600 flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignmentSelector;
